import { useEffect, useRef, useState, useCallback } from 'react';

const DEFAULT_FPS = 30;

/**
 * SwingPlayer — a single video with frame-accurate scrubbing and drawing overlay.
 *
 * Props:
 *   src             string  — video URL
 *   label           string  — header label (e.g. "Swing A")
 *   onReady         (meta)  — fires when video metadata loaded
 *   onTimeChange    (time)  — fires on every frame tick
 *   syncTime        number  — the user-marked sync point (e.g. impact) — display only
 *   endTime         number  — the user-marked loop-end point (optional) — display only
 *   drawings        array   — list of shape objects
 *   onDrawingsChange        — called when user modifies drawings
 *   activeTool      string  — 'none' | 'line' | 'circle' | 'angle'
 *   activeColor     string  — hex
 *   playbackRate    number
 *   onFpsDetected   (fps)   — when we figure out the video's fps
 * 
 * Exposes via ref:
 *   play(), pause(), seek(t), stepFrame(dir), getTime(), getDuration(), getFps()
 */
export default function SwingPlayer({
  src, label,
  onReady, onTimeChange,
  syncTime, endTime,
  drawings, onDrawingsChange,
  activeTool = 'none', activeColor = '#f4a948',
  playbackRate = 1,
  innerRef
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [fps, setFps] = useState(DEFAULT_FPS);
  const [loaded, setLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const drawingRef = useRef({ drawing: false, current: null, points: [] });

  // Keep the latest onTimeChange reachable from inside the long-lived
  // requestVideoFrameCallback loop (which is set up once per src). Without
  // this, the frame callback would close over the initial prop and miss
  // every subsequent update from the parent (e.g. a new end-loop marker).
  const onTimeChangeRef = useRef(onTimeChange);
  useEffect(() => { onTimeChangeRef.current = onTimeChange; });

  // Expose imperative API
  useEffect(() => {
    if (!innerRef) return;
    innerRef.current = {
      play: () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = true; // belt-and-suspenders for mobile autoplay policy
        const p = v.play();
        if (p && typeof p.catch === 'function') {
          p.catch(err => console.warn(`[${label}] play() rejected:`, err));
        }
      },
      pause: () => videoRef.current?.pause(),
      seek: (t) => { if (videoRef.current) videoRef.current.currentTime = Math.max(0, Math.min(duration, t)); },
      stepFrame: (dir) => {
        if (!videoRef.current) return;
        videoRef.current.pause();
        const step = 1 / fps;
        videoRef.current.currentTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + dir * step));
      },
      getTime: () => videoRef.current?.currentTime ?? 0,
      getDuration: () => duration,
      getFps: () => fps,
      setPlaybackRate: (r) => { if (videoRef.current) videoRef.current.playbackRate = r; }
    };
  }, [duration, fps, innerRef, label]);

  // Load metadata + fps detection. Also forces `muted` imperatively:
  // React's JSX `muted` attribute is famously unreliable at reflecting
  // into the DOM property, and on iOS/Android two videos can't play
  // from a single tap unless both are muted.
  useEffect(() => {
    if (!src) return;
    setLoaded(false);
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;

    const handleMeta = () => {
      setDuration(video.duration);
      setLoaded(true);
      onReady?.({ duration: video.duration });
      sizeCanvas();
    };
    video.addEventListener('loadedmetadata', handleMeta);
    return () => video.removeEventListener('loadedmetadata', handleMeta);
  }, [src]);

  // Frame callback for precise time tracking + fps detection
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !('requestVideoFrameCallback' in video)) return;

    let handle;
    let lastMediaTime = 0;
    let lastFrameNum = 0;
    let fpsSamples = [];

    const onFrame = (_now, metadata) => {
      setCurrentTime(video.currentTime);
      onTimeChangeRef.current?.(video.currentTime);

      // FPS detection: delta mediaTime / delta presentedFrames
      if (metadata.presentedFrames && metadata.mediaTime) {
        if (lastFrameNum > 0 && metadata.presentedFrames > lastFrameNum) {
          const dFrames = metadata.presentedFrames - lastFrameNum;
          const dTime = metadata.mediaTime - lastMediaTime;
          if (dTime > 0) {
            const sample = dFrames / dTime;
            if (sample > 10 && sample < 300) {
              fpsSamples.push(sample);
              if (fpsSamples.length > 10) fpsSamples.shift();
              const avg = fpsSamples.reduce((a, b) => a + b, 0) / fpsSamples.length;
              const rounded = Math.round(avg);
              if (Math.abs(rounded - fps) >= 1 && fpsSamples.length >= 3) {
                setFps(rounded);
              }
            }
          }
        }
        lastMediaTime = metadata.mediaTime;
        lastFrameNum = metadata.presentedFrames;
      }

      handle = video.requestVideoFrameCallback(onFrame);
    };

    handle = video.requestVideoFrameCallback(onFrame);
    return () => { if (handle) video.cancelVideoFrameCallback(handle); };
  }, [src]);

  // Apply playback rate changes
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  // Track play/pause state
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const on = () => setIsPlaying(true);
    const off = () => setIsPlaying(false);
    v.addEventListener('play', on);
    v.addEventListener('pause', off);
    return () => {
      v.removeEventListener('play', on);
      v.removeEventListener('pause', off);
    };
  }, []);

  // ----- Canvas sizing -----
  const sizeCanvas = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const rect = video.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    redraw();
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver(sizeCanvas);
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [sizeCanvas]);

  // ----- Drawing -----
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const drawShape = (d) => {
      ctx.strokeStyle = d.color;
      ctx.lineWidth = 2.5;
      ctx.fillStyle = d.color + '33';

      // Convert normalized 0-1 coords to canvas pixels
      const pts = d.points.map(p => ({ x: p.x * canvas.width, y: p.y * canvas.height }));

      if (d.tool === 'line' && pts.length >= 2) {
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        ctx.lineTo(pts[1].x, pts[1].y);
        ctx.stroke();
        // small dot at each end
        pts.forEach(p => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
          ctx.fill();
        });
      } else if (d.tool === 'circle' && pts.length >= 2) {
        const r = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
        ctx.beginPath();
        ctx.arc(pts[0].x, pts[0].y, r, 0, Math.PI * 2);
        ctx.stroke();
      } else if (d.tool === 'angle' && pts.length >= 2) {
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        ctx.lineTo(pts[1].x, pts[1].y);
        ctx.stroke();
        const dx = pts[1].x - pts[0].x;
        const dy = pts[1].y - pts[0].y;
        const deg = Math.round(Math.atan2(-dy, dx) * 180 / Math.PI);
        ctx.fillStyle = d.color;
        ctx.font = '600 13px "JetBrains Mono", monospace';
        ctx.fillText(`${deg}°`, pts[1].x + 8, pts[1].y - 8);
      }
    };

    (drawings || []).forEach(drawShape);
    if (drawingRef.current.current) drawShape(drawingRef.current.current);
  }, [drawings]);

  useEffect(() => { redraw(); }, [drawings, redraw]);

  const getNormalizedPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height
    };
  };

  const onMouseDown = (e) => {
    if (activeTool === 'none' || !loaded) return;
    const pos = getNormalizedPos(e);
    drawingRef.current = {
      drawing: true,
      current: { tool: activeTool, color: activeColor, points: [pos, pos] }
    };
    redraw();
  };
  const onMouseMove = (e) => {
    if (!drawingRef.current.drawing) return;
    drawingRef.current.current.points[1] = getNormalizedPos(e);
    redraw();
  };
  const onMouseUp = () => {
    if (!drawingRef.current.drawing) return;
    const shape = drawingRef.current.current;
    drawingRef.current = { drawing: false, current: null };
    if (shape && shape.points.length >= 2) {
      onDrawingsChange?.([...(drawings || []), shape]);
    }
    redraw();
  };

  const drawingActive = activeTool !== 'none' && loaded;

  return (
    <div
      ref={wrapperRef}
      className="flex flex-col rounded-lg overflow-hidden bg-fairway-900 border border-fairway-800 mx-auto w-full"
      // Cap the player's width so the 9:16 video area scales by viewport
      // height — no side-letterboxing for portrait swings on typical screens.
      style={{ maxWidth: 'calc((100vh - 360px) * 9 / 16)' }}
    >
      {/* Header */}
      <div className="px-3 py-2 flex justify-between items-center bg-fairway-900/80 border-b border-fairway-800">
        <span className="text-[11px] uppercase tracking-[0.15em] text-ink-300 font-medium">{label}</span>
        <span className="monospace text-[11px] text-ink-400">
          {currentTime.toFixed(3)}s / {duration.toFixed(2)}s · {fps}fps
        </span>
      </div>

      {/* Video + canvas — 9:16 portrait frame */}
      <div className="relative bg-black aspect-[9/16] flex items-center justify-center">
        <video
          ref={videoRef}
          src={src}
          playsInline
          muted
          preload="auto"
          crossOrigin="anonymous"
          className="w-full h-full object-contain"
        />
        <canvas
          ref={canvasRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          className="absolute inset-0 w-full h-full"
          style={{
            pointerEvents: drawingActive ? 'auto' : 'none',
            cursor: drawingActive ? 'crosshair' : 'default'
          }}
        />
        {!src && (
          <div className="absolute inset-0 flex items-center justify-center text-ink-400 text-sm">
            No video loaded
          </div>
        )}
      </div>

      {/* Timeline + per-video controls */}
      <div className="px-3 py-2 bg-fairway-900/80 border-t border-fairway-800 space-y-2">
        <div className="relative">
          <input
            type="range"
            className="timeline"
            min="0"
            max={duration || 100}
            step="0.01"
            value={currentTime}
            disabled={!loaded}
            onChange={(e) => {
              if (videoRef.current) videoRef.current.currentTime = parseFloat(e.target.value);
            }}
          />
          {syncTime != null && duration > 0 && (
            <div
              className="absolute top-1/2 -translate-y-1/2 w-[2px] h-3 bg-accent-flag pointer-events-none"
              style={{ left: `${(syncTime / duration) * 100}%` }}
              title={`Sync point @ ${syncTime.toFixed(3)}s`}
            />
          )}
          {endTime != null && duration > 0 && (
            <div
              className="absolute top-1/2 -translate-y-1/2 w-[2px] h-3 bg-cream-300 pointer-events-none"
              style={{ left: `${(endTime / duration) * 100}%` }}
              title={`Loop end @ ${endTime.toFixed(3)}s`}
            />
          )}
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          <IconBtn onClick={() => innerRef.current?.stepFrame(-1)} disabled={!loaded} title="Previous frame (,)">
            {'⟨'}
          </IconBtn>
          <IconBtn onClick={() => innerRef.current?.stepFrame(1)} disabled={!loaded} title="Next frame (.)">
            {'⟩'}
          </IconBtn>
          {syncTime != null && (
            <span className="monospace text-[10px] text-accent-flag ml-2" title={`Sync @ ${syncTime.toFixed(3)}s`}>
              ● {syncTime.toFixed(2)}s
            </span>
          )}
          {endTime != null && (
            <span className="monospace text-[10px] text-cream-300 ml-2" title={`End @ ${endTime.toFixed(3)}s`}>
              ▮ {endTime.toFixed(2)}s
            </span>
          )}
          <div className="flex-1" />
          <button
            onClick={() => onDrawingsChange?.([])}
            disabled={!drawings || drawings.length === 0}
            className="px-2.5 py-1 rounded text-[11px] uppercase tracking-wider text-ink-300 hover:text-ink-100 hover:bg-fairway-800 disabled:opacity-30 transition"
          >
            Clear draw
          </button>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ children, ...props }) {
  return (
    <button
      {...props}
      className="w-7 h-7 flex items-center justify-center rounded bg-fairway-800 hover:bg-fairway-700 text-ink-100 disabled:opacity-30 transition font-mono"
    >
      {children}
    </button>
  );
}
